import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { FaTimes, FaUserCircle, FaSpinner } from "react-icons/fa";

const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;

export default function ShareModal({ folder, onClose }) {
  const token = sessionStorage.getItem("userToken");
  const [email, setEmail] = useState("");
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch current access grants
  const fetchGrants = async () => {
    if (!folder?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/ocr/folder-access/`, {
        params: { folder_id: folder.id },
        headers: { Authorization: `Bearer ${token}` },
      });
      setGrants(res.data.access_grants || []);
    } catch (err) {
      console.error("Error fetching access grants", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGrants();
  }, [folder?.id]);

  // Autocomplete search
  useEffect(() => {
    if (email.trim().length < 1) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${BASE_URL}/ocr/org-users/`, {
          params: { q: email.trim() },
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuggestions(res.data || []);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      }
    }, 250); // debounce
    return () => clearTimeout(timer);
  }, [email]);

  // Close suggestions on outside click
  useEffect(() => {
    function handleClick(e) {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Grant access
  const handleShare = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSharing(true);
    setError("");
    setSuccess("");

    try {
      const res = await axios.post(
        `${BASE_URL}/ocr/folder-access/`,
        { folder_id: folder.id, user_email: email.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(res.data.message);
      setEmail("");
      fetchGrants();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to share.");
    } finally {
      setSharing(false);
    }
  };

  // Revoke access
  const handleRevoke = async (userEmail) => {
    setError("");
    setSuccess("");
    try {
      await axios.delete(`${BASE_URL}/ocr/folder-access/`, {
        data: { folder_id: folder.id, user_email: userEmail },
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess("Access revoked.");
      fetchGrants();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to revoke.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Share "{folder?.folder_name}"
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <FaTimes className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Add people form */}
          <form onSubmit={handleShare} className="relative">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                placeholder="Search by name or email..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <button
                type="submit"
                disabled={sharing || !email.trim()}
                className="px-4 py-2 bg-green-700 text-white text-sm font-medium rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sharing ? <FaSpinner className="animate-spin" /> : "Share"}
              </button>
            </div>

            {/* Autocomplete dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div
                ref={suggestionsRef}
                className="absolute left-0 right-16 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto"
              >
                {suggestions.map((user) => (
                  <button
                    key={user.email}
                    type="button"
                    onClick={() => {
                      setEmail(user.email);
                      setShowSuggestions(false);
                    }}
                    className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                  >
                    <FaUserCircle className="w-6 h-6 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{user.fullName}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                    <span className="ml-auto text-xs text-gray-400">{user.role}</span>
                  </button>
                ))}
              </div>
            )}
          </form>

          {/* Feedback messages */}
          {error && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
          )}
          {success && (
            <p className="text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg">{success}</p>
          )}

          {/* Divider */}
          <div className="border-t border-gray-200 pt-3">
            <p className="text-sm font-medium text-gray-700 mb-3">People with access</p>

            {/* Owner */}
            <div className="flex items-center gap-3 py-2">
              <FaUserCircle className="w-8 h-8 text-green-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{folder?.email}</p>
                <p className="text-xs text-gray-500">Owner</p>
              </div>
            </div>

            {/* Shared users */}
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <FaSpinner className="animate-spin text-gray-400" />
              </div>
            ) : (
              grants.map((grant) => (
                <div key={grant.id} className="flex items-center gap-3 py-2">
                  <FaUserCircle className="w-8 h-8 text-gray-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {grant.user_name || grant.user_email}
                    </p>
                    <p className="text-xs text-gray-500">
                      Shared by {grant.granted_by_email} · {new Date(grant.granted_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRevoke(grant.user_email)}
                    className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                    title="Revoke access"
                  >
                    <FaTimes className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
            )}

            {!loading && grants.length === 0 && (
              <p className="text-sm text-gray-400 py-2">No one else has access yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
